import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useApp } from "../context/AppContext";

const CartSheet = ({ open, onOpenChange }) => {
  const { cart, t, removeFromCart, updateQty, cartTotal } = useApp();
  const navigate = useNavigate();

  const checkout = () => {
    onOpenChange(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-cream w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-wine flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> {t("common.viewCart")}
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center text-ink/60 gap-4">
            <ShoppingBag className="w-12 h-12 text-wine/30" />
            <p>{t("common.emptyCart")}</p>
            <Button onClick={() => onOpenChange(false)} className="bg-wine hover:bg-wine-dark text-white">
              {t("common.continueShopping")}
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-2 px-2 my-4">
              <div className="space-y-4">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-3 bg-white rounded-xl p-3 border border-border">
                    {item.img ? (
                      <img src={item.img} alt={item.name} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo_moc.png"; }} className="w-16 h-16 object-cover rounded-lg" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-wine/10 flex items-center justify-center text-wine font-serif">C</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-ink truncate">{item.name}</p>
                      <p className="text-sm text-wine font-semibold">{t("common.currency")} {item.price.toFixed(2)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => updateQty(item.id, item.qty - 1)} className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:bg-cream-dark transition-colors"><Minus className="w-3 h-3" /></button>
                        <span className="w-8 text-center text-sm">{item.qty}</span>
                        <button onClick={() => updateQty(item.id, item.qty + 1)} className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:bg-cream-dark transition-colors"><Plus className="w-3 h-3" /></button>
                        <button onClick={() => removeFromCart(item.id)} className="ml-auto text-ink/40 hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex justify-between font-serif text-lg">
                <span>{t("common.subtotal")}</span>
                <span className="text-wine font-semibold">{t("common.currency")} {cartTotal.toFixed(2)}</span>
              </div>
              <Button data-testid="cart-checkout-button" className="w-full bg-wine hover:bg-wine-dark text-white h-12 text-base" onClick={checkout}>
                {t("common.checkout")}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
